import { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useDebounce } from '@node-space/hooks'
import { formatNumberWithCommas } from '../../../utils/utils'
import { useRewardsSheetContext } from '../context/RewardsSheetProvider'
import { RewardFormValues } from '../types'

const DEFAULT_FORM_VALUES: RewardFormValues = {
  currency: '',
  amount: 100000,
}

// Only allows digits 0-9, no decimals or special characters
const NUMERIC_INPUT_REGEX = /^[0-9]*$/

export const useRewardsForm = () => {
  const { rewardsProducts } = useRewardsSheetContext()
  const { control, register, setValue } = useForm<RewardFormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
  })

  useEffect(() => {
    if (rewardsProducts?.length > 0) {
      setValue('currency', rewardsProducts[0]?.currency || '', { shouldValidate: true })
    }
  }, [rewardsProducts])

  const formValues = useWatch({ control })
  const selectedCurrency = formValues.currency
  const amount = formValues.amount

  const debouncedAmount = useDebounce(amount, 500)

  const onAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // Remove commas from input value
    const numericValue = value.replace(/,/g, '')
    // Only allow digits 0-9
    if (NUMERIC_INPUT_REGEX.test(numericValue)) {
      setValue('amount', numericValue === '' ? 0 : Number(numericValue))
    }
  }

  const onCurrencyChange = (value: string) => {
    setValue('currency', value)
  }

  return {
    formValues,
    register,
    selectedCurrency,
    amount: debouncedAmount,
    rawAmount: amount,
    formattedAmount: formatNumberWithCommas(amount),
    onAmountChange,
    onCurrencyChange,
  }
}
